'use client'

import {Button, Pastil, SanityImage, SmallText} from '@local/ui/components'
import {cx} from '@local/utils/cx'

import {SanityImageType} from '@local/types'
import {ForwardedRef, forwardRef} from 'react'
import {useTranslate} from '@local/hooks'
import {formatPrice} from '@local/utils/currency'

export interface VariantCardProps {
	className?: string
	variants:Array<{
		gid: string
		title: string
		option1: string
		option2: string
		inventory: {isAvailable: boolean}
		peoplePerRoom: number | null
		price: number
	}>
	title?: string
	excerpt: string
	description: string
	imageTag?: {
		text: string
		variant: 'filled' | 'outlined'
	}
	gallery: Array<SanityImageType>
	uniqueItems: string
	notIncludedItems: string
	index: number
	onClick: (e) => void
}

export const VariantCard = forwardRef(
	(
		{
			className,
			variants,
			title,
			excerpt,
			gallery,
			imageTag,
			onClick,
		}: VariantCardProps,
		ref: ForwardedRef<HTMLElement>,
	) => {
		const coverImage = gallery[0]
		const locale = useTranslate()
		const variant = variants[0]
		title = title || variant?.option1

		return (
			<>
				<article
					ref={ref}
					className={cx(
						'shrink-0 relative span-w-10 lg:span-w-4 overflow-clip group',
						className,
					)}>
					<figure className='relative'>
						<SanityImage
							image={coverImage}
							width={452}
							height={600}
							desktopWidth={452}
							className='span-w-4 h-full lg:aspect-[452/600] object-cover'
							desktopOnly />
						<SanityImage
							image={coverImage}
							width={294}
							height={400}
							mobileWidth={294}
							className='span-w-10 h-full aspect-[294/400] object-cover'
							mobileOnly />

						<Button
							small
							variant='primary'
							onClick={onClick}
							className={cx(
								'absolute bottom-12 mx-12 left-0 right-0 lg-max:text-11',
								'transition-[background-color,color,border,opacity]',
								'lg:opacity-0 lg:group-hover:opacity-100 lg:focus-visible:opacity-100',
							)}>
							{locale('See details')}
						</Button>
					</figure>
					<div className='relative'>
						{title && <h3 className='text-16 lg:text-18 leading-snug tracking-tight my-12 lg:mt-20 uppercase'>{title}</h3>}
						{excerpt && <SmallText textColor='grey-2'>{excerpt}</SmallText>}
						{variant?.price && (
							<p className='text-9 md:text-12 leading-none tracking-doubleWide font-medium uppercase mt-12'>
								<span>{locale('Prices starting from')} </span>
								<span>{formatPrice(variant.price, 'nl-NL', undefined, {minimumFractionDigits: 0, maximumFractionDigits: 0}).replace(/\s/g, '')}</span>
								<span> / </span>
								<span>{locale('Per Person')}</span>
							</p>
						)}
					</div>
					{imageTag?.text && <Pastil variant={imageTag.variant} className='absolute top-20 left-1/2 -translate-x-1/2'>{imageTag.text}</Pastil>}
				</article>
			</>
		)
	},
)
